import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { fmt, fmtDate, gbErr, FLAG_LABEL, MAPS } from './gbAdminUtils';

export default function GbMatches({ seasonId, flaggedOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/gb/season/${seasonId}/matches`,
        { params: { flagged: flaggedOnly, limit: 200 } });
      setRows(data.matches || []);
    } catch (e) { toast.error(gbErr(e)); } finally { setLoading(false); }
  }, [seasonId, flaggedOnly]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-2" data-testid={flaggedOnly ? 'gb-admin-flagged-matches' : 'gb-admin-matches'}>
      <p className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500">
        {flaggedOnly && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
        {flaggedOnly ? 'Partidas marcadas pelo sistema' : 'Últimas partidas'} · {rows.length}
      </p>
      {rows.length === 0 && (
        <p className="rounded-xl border p-4 text-center text-xs text-slate-400">
          {flaggedOnly ? 'Nenhuma partida marcada.' : 'Nenhuma partida registrada.'}
        </p>
      )}
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              {['Quando', 'KAKO', 'Fase', 'Mapa', 'Status', 'Resultado', 'Duração', 'Tiros', 'Prêmio', 'Marcações'].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-black">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.match_id} className="border-t" data-testid={`gb-match-${m.match_id}`}>
                <td className="px-2 py-1.5 text-slate-500">{fmtDate(m.created_at)}</td>
                <td className="px-2 py-1.5 font-bold text-slate-700">{m.kako_id}</td>
                <td className="px-2 py-1.5">{m.node}</td>
                <td className="px-2 py-1.5 text-slate-500">{MAPS[m.map] || m.map}</td>
                <td className="px-2 py-1.5">
                  <span className={`rounded-full px-2 py-0.5 font-bold ${
                    m.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {m.status === 'open' ? 'em andamento' : 'encerrada'}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-bold">
                  {m.result ? (m.result.won ? <span className="text-emerald-600">vitória</span>
                    : <span className="text-rose-600">derrota</span>) : '—'}
                </td>
                <td className="px-2 py-1.5">{m.result?.duration ? `${m.result.duration}s` : '—'}</td>
                <td className="px-2 py-1.5">{m.result?.shots ?? '—'}</td>
                <td className="px-2 py-1.5 font-black text-cyan-700">
                  {m.result ? `💎 ${fmt(m.result.reward)}` : '—'}
                </td>
                <td className="px-2 py-1.5">
                  <span className="flex flex-wrap gap-1">
                    {(m.result?.flags || []).map((f) => (
                      <span key={f} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                        {FLAG_LABEL[f] || f}
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
